import { useCallback, useState } from "react";
import { Link, useSearchParams } from "react-router-dom";
import { useTranslation } from "react-i18next";
import AppHeader from "@/components/feature/AppHeader";
import AppFooter from "@/components/feature/AppFooter";
import AdminStats from "@/pages/admin/components/AdminStats";
import ApplicationReview from "@/pages/admin/components/ApplicationReview";
import LicenseManager from "@/pages/admin/components/LicenseManager";
import { useAdminData } from "@/pages/admin/hooks/useAdminData";
import { usePermissions } from "@/hooks/usePermissions";
import { downloadAdminLicenseReport } from "@/lib/adminLicenseExport";

type Tab = "applications" | "licenses";

interface Notice {
  text: string;
  tone: "success" | "error";
}

export default function AdminPage() {
  const { t } = useTranslation();
  const {
    applications,
    licenses,
    loading,
    error,
    reload,
    approve,
    reject,
    approveMany,
    rejectMany,
    renew,
    renewMany,
    revoke,
    restore,
    setOpenLimit,
  } = useAdminData();
  const { can } = usePermissions();
  const moduleLinks = [
    { to: "/admin/products", labelKey: "adminHome.moduleProducts", icon: "ri-apps-2-line", module: "products" as const },
    { to: "/admin/tools", labelKey: "adminHome.moduleTools", icon: "ri-tools-line", module: "licenses" as const },
    { to: "/admin/reports", labelKey: "adminHome.moduleReports", icon: "ri-file-chart-line", module: "reports" as const },
    { to: "/admin/customers", labelKey: "adminHome.moduleCustomers", icon: "ri-group-line", module: "customers" as const },
    { to: "/admin/audit", labelKey: "adminHome.moduleAudit", icon: "ri-file-history-line", module: "audit" as const },
    { to: "/admin/monitor", labelKey: "adminHome.moduleMonitor", icon: "ri-line-chart-line", module: "monitor" as const },
    { to: "/admin/staff", labelKey: "adminHome.moduleStaff", icon: "ri-shield-star-line", module: "staff" as const },
    { to: "/admin/apikeys", labelKey: "adminHome.moduleApiKeys", icon: "ri-plug-2-line", module: "apikeys" as const },
    { to: "/admin/approvals", labelKey: "adminHome.moduleApprovals", icon: "ri-flow-chart", module: "approvals" as const },
    { to: "/admin/help", labelKey: "adminHome.moduleHelp", icon: "ri-questionnaire-line", module: "help" as const },
  ].filter((item) => can(item.module));
  const [searchParams] = useSearchParams();
  const initialTab: Tab = searchParams.get("tab") === "licenses" ? "licenses" : "applications";
  const [tab, setTab] = useState<Tab>(initialTab);
  const [notice, setNotice] = useState<Notice | null>(null);

  const filterParam = searchParams.get("filter");
  const windowParam = Number(searchParams.get("window"));
  const initialLicenseFilter = filterParam === "expiring" ? ("expiring" as const) : ("all" as const);
  const initialLicenseWindow = Number.isFinite(windowParam) && windowParam > 0 ? windowParam : 30;

  const notify = useCallback((text: string, tone: "success" | "error" = "success") => {
    setNotice({ text, tone });
    window.setTimeout(() => setNotice(null), 4000);
  }, []);

  const pendingCount = applications.filter((item) => item.status === "pending").length;

  const handleExport = useCallback(() => {
    if (licenses.length === 0) {
      notify(t("adminHome.exportEmpty"), "error");
      return;
    }
    const count = downloadAdminLicenseReport(licenses);
    notify(t("adminHome.exportDone", { num: count }), "success");
  }, [licenses, notify, t]);

  return (
    <div className="flex min-h-screen flex-col bg-background-50">
      <AppHeader />

      <main className="flex-1">
        <section className="border-b border-background-200 bg-background-100">
          <div className="mx-auto w-full max-w-6xl px-4 py-12 md:px-6 md:py-16">
            <nav className="flex items-center gap-1.5 text-xs text-foreground-500">
              <Link to="/" className="cursor-pointer transition-colors hover:text-foreground-950">
                {t("adminHome.breadcrumbHome")}
              </Link>
              <span className="flex h-3 w-3 items-center justify-center">
                <i className="ri-arrow-right-s-line" />
              </span>
              <span className="text-foreground-800">{t("adminHome.title")}</span>
            </nav>
            <div className="mt-4 flex flex-col justify-between gap-4 md:flex-row md:items-end">
              <div>
                <span className="inline-flex items-center gap-2 rounded-full bg-primary-100 px-3 py-1 text-xs font-medium text-primary-700">
                  <span className="flex h-3 w-3 items-center justify-center">
                    <i className="ri-shield-user-line" />
                  </span>
                  {t("adminHome.badge")}
                </span>
                <h1 className="mt-3 font-heading text-2xl font-semibold tracking-tight text-foreground-950 md:text-4xl">
                  {t("adminHome.title")}
                </h1>
                <p className="mt-3 max-w-2xl text-sm leading-relaxed text-foreground-600 md:text-base">
                  {t("adminHome.subtitle")}
                </p>
              </div>
              <div className="flex flex-col items-start gap-3 md:items-end">
                {pendingCount > 0 && (
                  <div className="inline-flex items-center gap-2 rounded-lg border border-accent-200 bg-accent-50 px-4 py-3">
                    <span className="flex h-8 w-8 items-center justify-center rounded-md bg-accent-100 text-accent-700">
                      <i className="ri-notification-3-line" />
                    </span>
                    <span className="text-sm text-foreground-800">
                      {t("adminHome.pendingPrefix")}{' '}
                      <strong className="text-accent-800">{pendingCount}</strong>{' '}
                      {t("adminHome.pendingSuffix")}
                    </span>
                  </div>
                )}

                <div className="flex flex-wrap items-center gap-2">
                  {moduleLinks.map((item) => (
                    <Link
                      key={item.to}
                      to={item.to}
                      className="inline-flex cursor-pointer items-center gap-1.5 whitespace-nowrap rounded-md border border-background-300 bg-background-50 px-4 py-2.5 text-sm font-medium text-foreground-800 transition-colors hover:border-primary-300 hover:bg-background-100"
                    >
                      <span className="flex h-4 w-4 items-center justify-center text-primary-700">
                        <i className={item.icon} />
                      </span>
                      {t(item.labelKey)}
                    </Link>
                  ))}
                  <button
                    type="button"
                    onClick={handleExport}
                    className="inline-flex cursor-pointer items-center gap-1.5 whitespace-nowrap rounded-md border border-background-300 bg-background-50 px-4 py-2.5 text-sm font-medium text-foreground-800 transition-colors hover:border-primary-300 hover:bg-background-100"
                  >
                    <span className="flex h-4 w-4 items-center justify-center text-primary-700">
                      <i className="ri-download-2-line" />
                    </span>
                    {t("adminHome.exportAll")}
                  </button>
                </div>
              </div>
            </div>
          </div>
        </section>

        <section className="mx-auto w-full max-w-6xl px-4 py-10 md:px-6 md:py-14">
          {error ? (
            <div className="flex flex-col items-center gap-3 rounded-lg border border-background-200 bg-background-100 py-16 text-center">
              <span className="flex h-11 w-11 items-center justify-center rounded-md bg-accent-100 text-accent-700">
                <i className="ri-error-warning-line text-xl" />
              </span>
              <p className="text-sm text-foreground-600">{error}</p>
              <button
                type="button"
                onClick={reload}
                className="cursor-pointer rounded-md bg-primary-500 px-4 py-2 text-sm font-medium text-background-50 transition-colors hover:bg-primary-600"
              >
                {t("adminHome.reload")}
              </button>
            </div>
          ) : (
            <>
              <AdminStats applications={applications} licenses={licenses} />

              <div className="mt-8 mb-6 inline-flex rounded-full border border-background-300 bg-background-100 p-1">
                <button
                  type="button"
                  onClick={() => setTab("applications")}
                  className={`inline-flex cursor-pointer items-center gap-1.5 whitespace-nowrap rounded-full px-4 py-1.5 text-sm transition-colors ${
                    tab === "applications" ? "bg-background-50 text-foreground-950" : "text-foreground-600 hover:text-foreground-900"
                  }`}
                >
                  <span className="flex h-4 w-4 items-center justify-center">
                    <i className="ri-inbox-archive-line" />
                  </span>
                  {t("adminHome.tabApplications")}
                </button>
                <button
                  type="button"
                  onClick={() => setTab("licenses")}
                  className={`inline-flex cursor-pointer items-center gap-1.5 whitespace-nowrap rounded-full px-4 py-1.5 text-sm transition-colors ${
                    tab === "licenses" ? "bg-background-50 text-foreground-950" : "text-foreground-600 hover:text-foreground-900"
                  }`}
                >
                  <span className="flex h-4 w-4 items-center justify-center">
                    <i className="ri-key-2-line" />
                  </span>
                  {t("adminHome.tabLicenses")}
                </button>
              </div>

              {tab === "applications" ? (
                <ApplicationReview
                  applications={applications}
                  loading={loading}
                  onApprove={approve}
                  onReject={reject}
                  onApproveMany={approveMany}
                  onRejectMany={rejectMany}
                  notify={notify}
                />
              ) : (
                <LicenseManager
                  licenses={licenses}
                  loading={loading}
                  onRevoke={revoke}
                  onRestore={restore}
                  onRenew={renew}
                  onRenewMany={renewMany}
                  onSetOpenLimit={setOpenLimit}
                  notify={notify}
                  initialFilter={initialLicenseFilter}
                  initialWindow={initialLicenseWindow}
                />
              )}

              <div className="mt-8 flex flex-wrap items-center gap-3 rounded-lg border border-background-200 bg-secondary-50 px-5 py-4">
                <span className="flex h-9 w-9 items-center justify-center rounded-md bg-secondary-100 text-secondary-900">
                  <i className="ri-mail-send-line text-lg" />
                </span>
                <p className="text-xs leading-relaxed text-foreground-700">
                  {t("adminHome.tip")}
                </p>
              </div>
            </>
          )}
        </section>
      </main>

      {notice && (
        <div className="fixed bottom-6 left-1/2 z-[60] -translate-x-1/2 px-4">
          <div
            className={`lh-fade-up flex items-center gap-2.5 rounded-lg px-4 py-3 text-sm shadow-sm ${
              notice.tone === "success"
                ? "bg-primary-600 text-background-50"
                : "bg-accent-600 text-background-50"
            }`}
          >
            <span className="flex h-4 w-4 items-center justify-center">
              <i className={notice.tone === "success" ? "ri-checkbox-circle-line" : "ri-error-warning-line"} />
            </span>
            {notice.text}
          </div>
        </div>
      )}

      <AppFooter />
    </div>
  );
}